import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import Card from '../components/Card';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Input from '../components/Input';
import HamburgerMenu from '../components/HamburgerMenu';
import { formatDate } from '../utils/dateHelpers';
import { updateGame } from '../services/gameService';

const GameLog = () => {
  const navigate = useNavigate();
  const { games, categories, results, players, loading, isOrganizer, refreshCompetition, currentPlayer, competition } = useCompetition();

  const [editingGame, setEditingGame] = useState(null);
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Format duration from minutes to "2h 30m" format
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  // Get all played games (with or without duration)
  const playedGames = useMemo(() => {
    return games
      .filter(g => g.status === 'played')
      .map(game => {
        const category = categories.find(c => c.id === game.categoryId);

        // Get current player's result for this game
        const gameResults = results.filter(r => r.gameId === game.id && r.participated);
        const playerResult = gameResults.find(r => r.playerId === currentPlayer?.id);

        // Calculate position if player participated
        let position = null;
        let totalPlayers = gameResults.length;

        if (playerResult) {
          const sortedResults = gameResults
            .map(r => ({ ...r, points: r.pointsEntered || 0 }))
            .sort((a, b) => b.points - a.points);
          position = sortedResults.findIndex(r => r.playerId === currentPlayer.id) + 1;
        }

        return {
          ...game,
          category,
          playerResult,
          position,
          totalPlayers
        };
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date)); // Most recent first
  }, [games, categories, results, currentPlayer]);

  // Find suggested duration from other games with the same name
  const getSuggestedDuration = (gameName, currentGameId) => {
    const sameNameGame = playedGames.find(
      g => g.name === gameName && g.id !== currentGameId && g.durationMinutes
    );
    return sameNameGame?.durationMinutes || null;
  };

  // Calculate total time per category
  const categoryTotals = useMemo(() => {
    const totals = {};

    playedGames.forEach(game => {
      if (game.categoryId && game.durationMinutes) {
        if (!totals[game.categoryId]) {
          totals[game.categoryId] = {
            category: game.category,
            totalMinutes: 0,
            gamesCount: 0
          };
        }
        totals[game.categoryId].totalMinutes += game.durationMinutes;
        totals[game.categoryId].gamesCount += 1;
      }
    });

    // Calculate average for each category
    return Object.values(totals)
      .map(total => ({
        ...total,
        averageMinutes: total.gamesCount > 0 ? Math.round(total.totalMinutes / total.gamesCount) : 0
      }))
      .sort((a, b) => b.totalMinutes - a.totalMinutes);
  }, [playedGames]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    return playedGames.reduce((sum, game) => sum + (game.durationMinutes || 0), 0);
  }, [playedGames]);

  const handleEditDuration = (game) => {
    if (!isOrganizer) return;

    const suggestedDuration = getSuggestedDuration(game.name, game.id);

    if (suggestedDuration) {
      const suggestedHours = Math.floor(suggestedDuration / 60);
      const suggestedMins = suggestedDuration % 60;
      setHours(suggestedHours > 0 ? suggestedHours.toString() : '');
      setMinutes(suggestedMins.toString());
    } else {
      setHours('');
      setMinutes('');
    }

    setEditingGame(game);
    setError('');
  };

  const handleSaveDuration = async () => {
    setError('');

    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;

    if (h === 0 && m === 0) {
      setError('Please enter a duration');
      return;
    }

    const totalMinutes = h * 60 + m;

    setSaving(true);
    try {
      await updateGame(editingGame.id, {
        durationMinutes: totalMinutes
      });

      await refreshCompetition();
      setEditingGame(null);
      setHours('');
      setMinutes('');
    } catch (err) {
      console.error('Error saving duration:', err);
      setError('Failed to save duration: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingGame(null);
    setHours('');
    setMinutes('');
    setError('');
  };

  const handleGameClick = (game) => {
    // If no duration and user is organizer, open duration editor
    if (!game.durationMinutes && isOrganizer) {
      handleEditDuration(game);
    } else {
      // Otherwise navigate to game details
      navigate(`/competition/${competition.id}/game/${game.id}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Loading game log...</p>
        </div>
      </div>
    );
  }

  const suggestedDuration = editingGame ? getSuggestedDuration(editingGame.name, editingGame.id) : null;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <HamburgerMenu />
      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="mb-6">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Game Log</p>
          <h1 className="text-3xl font-bold text-gray-900">Game History</h1>
          <p className="text-sm text-gray-600 mt-1">
            {playedGames.length} games played
            {grandTotal > 0 && ` • Total time: ${formatDuration(grandTotal)}`}
          </p>
        </div>

        {/* Category Totals */}
        <Card title="Time by Category">
          {categoryTotals.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-gray-500">No completed games yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {categoryTotals.map(({ category, totalMinutes, gamesCount, averageMinutes }) => (
                <div
                  key={category.id}
                  className="p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <div className="text-2xl sm:text-3xl flex-shrink-0">{category.icon}</div>
                      <div className="min-w-0">
                        <div className="font-semibold text-base sm:text-lg truncate">{category.name}</div>
                        <div className="text-xs sm:text-sm text-gray-600">
                          {gamesCount} {gamesCount === 1 ? 'game' : 'games'} played
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xl sm:text-2xl font-bold text-blue-700">
                        {formatDuration(totalMinutes)}
                      </div>
                      <div className="text-xs text-gray-500">total</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                    <div className="text-xs sm:text-sm text-gray-600">Average per game</div>
                    <div className="text-sm sm:text-base font-semibold text-blue-600">
                      {formatDuration(averageMinutes)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* All Games List */}
        <Card title="All Games">
          {playedGames.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">🎮</div>
              <p className="text-gray-500">No completed games yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {playedGames.map((game) => {
                const positionEmoji = game.position === 1 ? '🥇' :
                                     game.position === 2 ? '🥈' :
                                     game.position === 3 ? '🥉' :
                                     game.position ? `#${game.position}` : null;

                const positionColor = game.position === 1 ? 'text-yellow-600' :
                                     game.position === 2 ? 'text-gray-500' :
                                     game.position === 3 ? 'text-orange-600' :
                                     'text-gray-700';

                return (
                  <div
                    key={game.id}
                    onClick={() => handleGameClick(game)}
                    className="p-2 sm:p-3 rounded-lg border-2 border-gray-200 hover:border-blue-400 cursor-pointer hover:bg-blue-50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                        <div className="text-xl sm:text-2xl flex-shrink-0">{game.category?.icon || '🎮'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm sm:text-base truncate">{game.name}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {game.category?.name} • {formatDate(game.date)}
                            {game.knockoutMode && ' • 🎯 Knockout'}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        {game.durationMinutes ? (
                          <>
                            <div className="text-base sm:text-xl font-bold text-gray-900">
                              {formatDuration(game.durationMinutes)}
                            </div>
                            <div className="text-xs text-gray-500">duration</div>
                          </>
                        ) : (
                          <div>
                            <div className="text-xs sm:text-sm text-gray-400 italic">No timer</div>
                            {isOrganizer && (
                              <div className="text-xs text-blue-600 mt-1">Tap to add</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Player's result */}
                    {game.playerResult ? (
                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                        <div className="text-xs sm:text-sm text-gray-600">Your result</div>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm sm:text-base font-bold ${positionColor}`}>
                            {positionEmoji}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-500">
                            {game.totalPlayers} players
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="pt-2 border-t border-gray-200">
                        <div className="text-xs text-gray-400 italic">Did not participate</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Version Info */}
        <div className="text-center py-4 text-xs text-gray-400">
          v{require('../../package.json').version}
        </div>
      </div>

      {/* Edit Duration Modal */}
      {editingGame && (
        <Modal
          isOpen={!!editingGame}
          onClose={handleCancel}
          title="Set Game Duration"
        >
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-700 font-medium mb-2">
                {editingGame.name}
              </p>
              <p className="text-xs text-gray-500">
                {editingGame.category?.name} • {formatDate(editingGame.date)}
              </p>
            </div>

            {suggestedDuration && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 Suggested duration based on previous "{editingGame.name}" game:{' '}
                  <span className="font-bold">{formatDuration(suggestedDuration)}</span>
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hours
                </label>
                <Input
                  type="number"
                  min="0"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minutes
                </label>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleCancel}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveDuration}
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Duration'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default GameLog;
